// A single-number "Graph confidence" badge for a finished discovery.
//
// IMPORTANT — this is NOT "accuracy". We never know the true graph for a
// user's own data (if they did, they wouldn't need the tool), so we cannot
// honestly claim correctness. What we CAN measure, on any dataset, is how
// STABLE + RESOLVED the result is:
//   - presence confidence: the mean bootstrap stability across all edges
//     (how often each relationship reappears when the data is resampled).
//   - directedness: the share of relationships the algorithm could commit
//     to a direction on (direction_confidence >= 0.8).
//
// The headline number is the mean presence confidence (0-100). The
// theoretical max is 100% but it's rarely reached: noise lowers stability,
// and Markov-equivalence makes some directions unresolvable without domain
// knowledge. A click on the ? opens a floating popover that spells this out
// so the number is never mistaken for "% correct".
//
// This card is a pure consumer of the server-computed `graph_confidence`
// object (backend `_compute_graph_confidence` is the single source of truth).
// It does NOT recompute anything from the edge list.

import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { Discovery } from '@/lib/discoveries';
import { HelpCircle } from 'lucide-react';

interface Props {
  discovery: Discovery;
}

// Tailwind classes by score band — emerald (trustworthy) / amber (mixed) /
// slate (weak). Plain string literals so the JIT compiler keeps them.
function band(score: number): { ring: string; text: string; bg: string; label: string } {
  if (score >= 75)
    return {
      ring: 'border-emerald-500/40',
      text: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-500/5',
      label: 'Strong',
    };
  if (score >= 50)
    return {
      ring: 'border-amber-500/40',
      text: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-500/5',
      label: 'Moderate',
    };
  return {
    ring: 'border-slate-400/40',
    text: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-500/5',
    label: 'Weak',
  };
}

export function GraphConfidenceCard({ discovery }: Props) {
  // Null when the result was S3-spilled or carried no numeric confidences —
  // hide the card entirely (same null-handling as before).
  const gc = discovery.graph_confidence;
  if (!gc) return null;

  const { score, total_edges: totalEdges, directed } = gc;
  const knowledgeEdges = gc.knowledge_edges ?? 0;
  const conflictedEdges = gc.conflicted_edges ?? 0;
  const scoreUnaided = gc.score_unaided ?? null;
  const b = band(score);

  return (
    <Card className={`${b.ring} ${b.bg} border`}>
      <CardContent className="py-4">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Graph confidence
          </span>
          <Popover>
            <PopoverTrigger
              aria-label="How is graph confidence calculated?"
              className="text-muted-foreground transition-colors hover:text-foreground focus:outline-none"
            >
              <HelpCircle className="h-3.5 w-3.5" />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 text-sm">
              <p className="text-muted-foreground">
                How trustworthy this graph is: how reliably each relationship
                holds up across resamples, plus relationships you've confirmed.
                Not a claim of correctness.
              </p>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-baseline gap-2">
          <span className={`text-3xl font-bold tabular-nums ${b.text}`}>{score}%</span>
          <span className={`text-sm font-medium ${b.text}`}>{b.label}</span>
        </div>
        {knowledgeEdges > 0 ? (
          <span className="mt-0.5 block text-xs text-muted-foreground">
            Includes {knowledgeEdges} relationship{knowledgeEdges === 1 ? '' : 's'} you confirmed
            — your knowledge counts as certain here.
          </span>
        ) : null}
        {knowledgeEdges > 0 && conflictedEdges > 0 ? (
          <span className="mt-0.5 block text-xs text-amber-600 dark:text-amber-400">
            {conflictedEdges} edge{conflictedEdges === 1 ? '' : 's'} you required aren't supported
            by the data.
          </span>
        ) : null}
        {knowledgeEdges > 0 && scoreUnaided !== null ? (
          <div className="mt-2 border-t pt-2">
            <span className="block text-sm font-semibold tabular-nums text-muted-foreground">
              Algorithm-only: {scoreUnaided}%
            </span>
            <span className="block text-xs text-muted-foreground">
              What the data alone supports, before your knowledge.
            </span>
          </div>
        ) : null}
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {directed} of {totalEdges} relationships confidently directed
        </span>
      </CardContent>
    </Card>
  );
}
